import { IBriefDeliverable } from "@shared/lib/types/briefInfluencerV2";
import {
  IDeliverableConfigurator,
  IPurchaseItems,
} from "@shared/lib/types/deliverableConfigurator";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Tooltip } from "@shared/ui";
import { Empty } from "antd";
import classNames from "classnames";
import { Shield } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

const getPackageLabel = (deliverable?: IBriefDeliverable) => {
  if (!deliverable) return "";

  return deliverable.name
    ? deliverable.name
    : deliverable.setNumber ? `Package ${deliverable.setNumber ?? ""}` : "";
};

const getFormatParts = (item: IPurchaseItems) => {
  const platform = item.contentDeliverableConfigurator?.platform ?? "";
  const category = item.contentCategory || "Format";

  const subTypeParts = Object.entries(item.contentSubType || {})
    .filter(([_, value]) => Boolean(value))
    .map(([key, value]) => `${key}: ${value}`);

  return { platform, category, subTypeParts };
};

const renderFormatLabel = (
  item: IPurchaseItems,
  {
    formatClassName,
    subTypeClassName,
  }: { formatClassName: string; subTypeClassName: string }
) => {
  const { category, subTypeParts } = getFormatParts(item);

  return (
    <>
      <span className={formatClassName}>{category}</span>
      {subTypeParts.length > 0 ? (
        <span className={subTypeClassName}>
          {` (${subTypeParts.join(", ")})`}
        </span>
      ) : null}
    </>
  );
};

const getAddRightsLabel = (item: IPurchaseItems) => {
  if (item.purchaseItemType === "Content") return null;

  const type = item.adRightsType || "Ad Rights";
  const duration = item.adRightsDurations?.trim();

  if (!duration) return type;

  return `${type} · ${duration}`;
};

const getSpecialNotes = (deliverable: IBriefDeliverable) => {
  const notes = deliverable.notes?.trim();

  if (!notes) return null;

  return (
    notes
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim() || null
  );
};

const renderSpecialNotes = (
  notes: string,
  { className, labelClassName }: { className: string; labelClassName: string }
) => (
  <p className={className}>
    <span className={labelClassName}>Special notes: </span>
    {notes}
  </p>
);

const renderAddRights = (label: string, rightsClassName: string) => (
  <>
    {" · "}
    <span className={rightsClassName}>
      <Shield size={10} className={styles.rightsIcon} aria-hidden />
      {label}
    </span>
  </>
);

interface FormatListItemStyleProps {
  listItemClassName: string;
  platformIconClassName: string;
  itemClassName: string;
  formatClassName: string;
  subTypeClassName: string;
  rightsClassName: string;
}

const getLinkedFormatLabel = (
  adRightsItem: IPurchaseItems,
  allPurchaseItems: IPurchaseItems[],
  configurators?: IDeliverableConfigurator[]
) => {
  const linkedId = adRightsItem.adRightsLinkedToContentPurchaseItemId;
  if (!linkedId) return null;

  const linkedItem = allPurchaseItems.find((item) => item._id === linkedId);
  if (!linkedItem) return null;

  const config = configurators?.find((c) => c._id === linkedItem.contentDeliverableConfiguratorId);
  const platform = linkedItem.contentDeliverableConfigurator?.platform || config?.platform || "";
  const category = linkedItem.contentCategory || "Format";

  if (platform) {
    return `${platform} - ${category}`;
  }
  return category;
};

const renderFormatListItem = (
  item: IPurchaseItems,
  index: number,
  {
    listItemClassName,
    platformIconClassName,
    itemClassName,
    formatClassName,
    subTypeClassName,
    rightsClassName,
  }: FormatListItemStyleProps,
  configurator?: IDeliverableConfigurator,
  allPurchaseItems?: IPurchaseItems[],
  configurators?: IDeliverableConfigurator[]
) => {
  const isContent = item.purchaseItemType !== "Ad-Rights";

  if (isContent) {
    const { platform } = getFormatParts(item);
    return (
      <li
        key={`${item.contentDeliverableConfiguratorDeliverableId}-${index}`}
        className={listItemClassName}
        style={{
          textDecoration: item.isCancelled ? "line-through" : "",
          textDecorationColor: item.isCancelled ? "red" : ""
        }}
      >
        <span className={platformIconClassName}>
          {getPlatformIcon(
            platform || configurator?.platform || "",
            styles.platformIconGlyph
          )}
        </span>
        <span className={itemClassName}>
          {renderFormatLabel(item, {
            formatClassName,
            subTypeClassName,
          })}
        </span>
      </li>
    );
  } else {
    const addRightsLabel = getAddRightsLabel(item);
    const linkedFormatName = allPurchaseItems
      ? getLinkedFormatLabel(item, allPurchaseItems, configurators)
      : null;

    return (
      <li
        key={`${item._id || index}-${index}`}
        className={listItemClassName}
        style={{
          textDecoration: item.isCancelled ? "line-through" : "",
          textDecorationColor: item.isCancelled ? "red" : ""
        }}
      >
        <span className={platformIconClassName} style={{ display: "inline-flex", alignItems: "center" }}>
          <Shield size={12} style={{ color: "rgb(99 91 255)" }} aria-hidden />
        </span>
        <span className={itemClassName}>
          <span className={formatClassName}>Ad Rights</span>
          {addRightsLabel ? (
            <span className={subTypeClassName}>
              {` (${addRightsLabel})`}
            </span>
          ) : null}
          {linkedFormatName ? (
            <span className={subTypeClassName} style={{ marginLeft: 4 }}>
              {` for ${linkedFormatName}`}
            </span>
          ) : null}
          {item.adRightsNotes ? (
            <span className={subTypeClassName} style={{ marginLeft: 4 }}>
              {` - Notes: ${item.adRightsNotes}`}
            </span>
          ) : null}
        </span>
      </li>
    );
  }
};

export interface DeliverablePillSummaryProps {
  deliverable: IBriefDeliverable;
  compact?: boolean;
  configurators?: IDeliverableConfigurator[];
  showTooltip?: boolean;
  showSpecialNotes?: boolean;
}

export function DeliverablePillSummary({
  deliverable,
  compact = false,
  configurators,
  showTooltip,
  showSpecialNotes = false,
}: DeliverablePillSummaryProps) {
  const packageLabel = getPackageLabel(deliverable);
  const formats = deliverable?.purchaseItems ?? [];
  const specialNotes = getSpecialNotes(deliverable);
  const [open, setOpen] = useState<boolean>(showTooltip ?? false);

  useEffect(() => {
    if (showTooltip !== undefined) {
      setOpen(showTooltip);
    }
  }, [showTooltip]);

  const pillContent = (
    <div
      className={classNames(styles.pill, {
        [styles.pillCompact]: compact,
      })}
    >
      {formats.length > 0 ? (
        <ul className={styles.list}>
          {formats.map((item, index) =>
            renderFormatListItem(
              item,
              index,
              {
                listItemClassName: styles.listItem,
                platformIconClassName: styles.platformIcon,
                itemClassName: styles.item,
                formatClassName: styles.format,
                subTypeClassName: styles.subType,
                rightsClassName: styles.rights,
              },
              configurators?.find(
                (configurator) =>
                  configurator._id === item.contentDeliverableConfiguratorId
              ),
              formats,
              configurators
            )
          )}
        </ul>
      ) : <Empty description="No deliverables found" style={{width: "100%"}}/>}
      {showSpecialNotes && specialNotes
        ? renderSpecialNotes(specialNotes, {
            className: styles.notes,
            labelClassName: styles.notesLabel,
          })
        : null}
    </div>
  );

  if (formats.length === 0 && !specialNotes) {
    return pillContent;
  }

  return (
    <Tooltip
      open={showTooltip !== undefined ? open : undefined}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
      }}
      trigger={["hover"]}
      destroyTooltipOnHide
      getPopupContainer={() => document.body}
      overlayClassName={styles.tooltipOverlay}
      title={
        <div className={styles.tooltip}>
          {packageLabel ? (
            <p className={styles.tooltipTitle}>{packageLabel}</p>
          ) : null}
          {formats.length > 0 ? (
            <ul className={styles.tooltipList}>
              {formats.map((item, index) =>
                renderFormatListItem(
                  item,
                  index,
                  {
                    listItemClassName: styles.tooltipListItem,
                    platformIconClassName: styles.tooltipPlatformIcon,
                    itemClassName: styles.tooltipItem,
                    formatClassName: styles.tooltipFormat,
                    subTypeClassName: styles.tooltipSubType,
                    rightsClassName: styles.tooltipRights,
                  },
                  configurators?.find(
                    (configurator) =>
                      configurator._id === item.contentDeliverableConfiguratorId
                  ),
                  formats,
                  configurators
                )
              )}
            </ul>
          ) : null}
          {specialNotes
            ? renderSpecialNotes(specialNotes, {
                className: styles.tooltipNotes,
                labelClassName: styles.tooltipNotesLabel,
              })
            : null}
        </div>
      }
    >
      {pillContent}
    </Tooltip>
  );
}
